import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { SnackbarProvider, useSnackbar, VariantType } from 'notistack';

interface NotificationContextType {
  showNotification: (message: string, variant?: VariantType) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationComponent: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const showNotification = useCallback((message: string, variant: VariantType = 'default') => {
    enqueueSnackbar(message, { variant });
  }, [enqueueSnackbar]);

  const showSuccess = useCallback((message: string) => {
    enqueueSnackbar(message, { variant: 'success' });
  }, [enqueueSnackbar]);

  const showError = useCallback((message: string) => {
    enqueueSnackbar(message, { variant: 'error' });
  }, [enqueueSnackbar]);

  const showWarning = useCallback((message: string) => {
    enqueueSnackbar(message, { variant: 'warning' });
  }, [enqueueSnackbar]);

  const showInfo = useCallback((message: string) => {
    enqueueSnackbar(message, { variant: 'info' });
  }, [enqueueSnackbar]);

  const value = useMemo(() => ({
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  }), [showNotification, showSuccess, showError, showWarning, showInfo]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      autoHideDuration={6000}
      dense
      preventDuplicate
    >
      <NotificationComponent>
        {children}
      </NotificationComponent>
    </SnackbarProvider>
  );
};
